import { Fragment, ReactNode } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DailyGridHeader } from "../../../types/sale-daily-grid.type";
import { formatNumber, numberWithCommas } from "../../../utils";
import { formatDateTimeBkk } from "../../../utils/date";
import {
  getDayColor,
  getStatusColor,
  getStatusLabel,
  isoDayToThai,
} from "./dailyGrid.utils";

interface DailyGridHeaderSummaryProps {
  header: DailyGridHeader;
  /** Number of store rows in the report (จำนวนร้าน) — derived from rows. */
  storeCount: number;
}

const DASH = "-";

/**
 * A negative ผลต่าง means trays did not come back. Same red the grid body uses
 * for its negative ผลต่าง figures, so the two read as one report.
 */
const NEGATIVE = "#c62828";

const text = (v: string | null | undefined): string =>
  v == null || v === "" ? DASH : v;

/**
 * Tray counts are the only nullable numbers in this header: check-ins that
 * predate จำนวนถาดขึ้น have no reading and are never backfilled. NULL must show
 * as a dash — `0 ถาด` would assert that no trays were loaded, which is a claim
 * the data does not make.
 */
const trayCount = (v: number | null | undefined): string =>
  v == null ? DASH : `${numberWithCommas(v)} ถาด`;

/**
 * Store counts (จำนวนร้าน / ร้านขายสด / ร้านขายเชื่อ) are nullable for the same
 * reason tray counts are: report versions written before the cash/credit split
 * have no stored value and are never backfilled. `0` there would claim the van
 * billed nobody, so NULL shows as a dash. No unit suffix — the labels already
 * say ร้าน.
 */
const storeCountText = (v: number | null | undefined): string =>
  v == null ? DASH : numberWithCommas(v);

/**
 * เวลากลับ is shown as วัน-เวลา, not time-only. It is a trip-level reading that can
 * cross midnight: a van back at 00:30 belongs to the NEXT calendar day, and a bare
 * "00:30:00" leaves branches guessing which day that was.
 *
 * Prefers the date-bearing `time_return_branch_at`; falls back to the legacy
 * time-only string when the backend predates that field, so an older API still
 * renders a time rather than a dash.
 */
const returnTimeText = (header: DailyGridHeader): string =>
  formatDateTimeBkk(header.time_return_branch_at) ||
  text(header.time_return_branch);

/**
 * Colours for one block of the header table. Branches read this header as a
 * spreadsheet, so every label and every value is a real bordered cell and the
 * label column is tinted differently from the value column.
 */
interface CellTheme {
  /** Background of the label (left) cell. */
  labelBg: string;
  /** Background of the value (right) cell. */
  valueBg: string;
  /** Gridline colour. */
  border: string;
}

const NEUTRAL: CellTheme = {
  labelBg: "#EDEEF4",
  valueBg: "#FFFFFF",
  border: "#C9CCDB",
};

const AMBER: CellTheme = {
  labelBg: "#FCE7C2",
  valueBg: "#FFFDF7",
  border: "#E3C28A",
};

const BLUE: CellTheme = {
  labelBg: "#DCE1FC",
  valueBg: "#FAFBFF",
  border: "#B6BFF5",
};

/** One label/value pair rendered as two adjacent spreadsheet cells. */
interface Field {
  label: string;
  value: ReactNode;
  valueColor?: string;
}

/**
 * A bordered spreadsheet block: `cols` label/value pairs per row, laid out on a
 * single grid so the gridlines line up across the whole block.
 *
 * Gridlines are drawn once each — the container supplies the top/left edges and
 * every cell supplies its own right/bottom edge — so adjacent cells share a
 * single 1px line instead of doubling up.
 */
const GridBlock = ({
  theme,
  cols,
  flex,
  fields,
}: {
  theme: CellTheme;
  cols: number;
  flex: string;
  fields: Field[];
}) => {
  const cellsPerRow = cols * 2;
  const remainder = (fields.length * 2) % cellsPerRow;
  // Pad the last row so the block always ends on a straight bottom edge.
  const fillerSpan = remainder === 0 ? 0 : cellsPerRow - remainder;

  const cellBase = {
    px: 0.75,
    py: 0.3,
    fontSize: "0.72rem",
    fontWeight: 600,
    lineHeight: 1.55,
    minWidth: 0,
    borderRight: `1px solid ${theme.border}`,
    borderBottom: `1px solid ${theme.border}`,
  } as const;

  return (
    <Box
      sx={{
        flex,
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, auto minmax(0, 1fr))`,
        borderTop: `1px solid ${theme.border}`,
        borderLeft: `1px solid ${theme.border}`,
        borderRadius: 0.5,
        overflow: "hidden",
        alignContent: "start",
      }}
    >
      {fields.map((field) => (
        <Fragment key={field.label}>
          <Box
            sx={{
              ...cellBase,
              bgcolor: theme.labelBg,
              color: "#4c4c5a",
              whiteSpace: "nowrap",
            }}
          >
            {field.label}
          </Box>
          <Box
            sx={{
              ...cellBase,
              bgcolor: theme.valueBg,
              color: field.valueColor ?? "#2a2a33",
              textAlign: "right",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {field.value}
          </Box>
        </Fragment>
      ))}
      {fillerSpan > 0 && (
        <Box
          sx={{
            ...cellBase,
            bgcolor: theme.valueBg,
            gridColumn: `span ${fillerSpan}`,
          }}
        />
      )}
    </Box>
  );
};

export default function DailyGridHeaderSummary({
  header,
  storeCount,
}: DailyGridHeaderSummaryProps) {
  const dayColor = getDayColor(header.day_of_week);

  const identityFields: Field[] = [
    {
      label: "วันที่ / วัน",
      value: `${text(header.report_date)} (${isoDayToThai(header.day_of_week)})`,
    },
    { label: "รหัสรายงาน", value: text(header.sales_report_code) },
    { label: "เขตการขาย", value: text(header.route_name) },
    { label: "สายส่ง", value: text(header.route_code) },
    { label: "พนักงานขาย", value: text(header.sales_staff_name) },
    { label: "ผู้ช่วยขาย", value: text(header.sales_assistant_name) },
    {
      label: "สถานะ",
      value: (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              bgcolor: getStatusColor(header.status),
            }}
          />
          {getStatusLabel(header.status)}
        </Box>
      ),
    },
  ];

  /*
   * The header's sale total is shown EX-VAT, so it reconciles against the เงินสด
   * and เงินเชื่อ cells beside it — both of which are already ex-VAT. Showing the
   * VAT-inclusive `total_price` here meant เงินสด + เงินเชื่อ never added up to
   * รวมยอดขาย, which is the mismatch branches reported.
   *
   * `total_before_vat` comes straight from the report snapshot (sum of each
   * bill's ex-VAT amount_total). The fallback is an exact identity, not an
   * estimate — per bill, amount_total === net_total − vat_total — and only
   * covers payloads from a backend that predates the field.
   */
  const totalBeforeVat =
    header.total_before_vat ?? header.total_price - header.total_vat;

  // ผลต่าง comes pre-computed from the report snapshot (ถาดกลับ − ถาดขึ้น) rather
  // than being derived here, so the card cannot disagree with the stored figure.
  const palletDiff = header.pallet_diff;
  const palletDiffColor =
    palletDiff != null && palletDiff < 0 ? NEGATIVE : undefined;

  // Quantities that are physical counts (ชิ้น) and odometer/mileage readings (km)
  // stay whole numbers; measured quantities (litres, weight) and money show 2dp.
  const moneyFields: Field[] = [
    {
      label: "รวมยอดเบิก",
      value: `${numberWithCommas(header.total_withdraw_qty)} ชิ้น`,
    },
    { label: "เงินสด", value: formatNumber(header.cash_total_value) },
    { label: "เงินเชื่อ", value: formatNumber(header.credit_total_value) },
    {
      label: "ของเหลือ",
      value: `${numberWithCommas(header.leftover_count_total)} ชิ้น`,
    },
    {
      label: "ของเสีย",
      value: `${numberWithCommas(header.waste_count_total)} ชิ้น`,
    },
    // Labelled "(ก่อน VAT)" on purpose: the grid body below has its own
    // รวมยอดขาย column footer that is VAT-inclusive, so a bare "รวมยอดขาย" here
    // would read as the same figure and look wrong by exactly the VAT.
    { label: "รวมยอดขาย (ก่อน VAT)", value: formatNumber(totalBeforeVat) },
    { label: "น้ำหนักของเสีย", value: `${formatNumber(header.waste_weigh)} kg` },
    { label: "% ของเสีย", value: `${formatNumber(header.waste_percent_total)}%` },
    { label: "VAT", value: formatNumber(header.total_vat) },
    // Trays close out this block rather than standing alone: branches reconcile
    // ถาด against ของเหลือ / ของเสีย (what physically came back on the van), not
    // against the trip block's fuel and odometer. Three fields = exactly one more
    // row of the 3-column grid, so the block stays rectangular.
    { label: "ถาดขึ้น", value: trayCount(header.pallet_out) },
    { label: "ถาดกลับ", value: trayCount(header.pallet_in) },
    {
      label: "ผลต่าง",
      value: trayCount(palletDiff),
      valueColor: palletDiffColor,
    },
  ];

  const tripFields: Field[] = [
    { label: "เติมน้ำมัน", value: `${formatNumber(header.fuel_bath)} บาท` },
    { label: "ไมล์ออก", value: `${numberWithCommas(header.miles_out)} km` },
    // Litres is a measured quantity — it must keep its decimals (was rounded to
    // a whole number by numberWithCommas, which branches reported as wrong).
    { label: "จำนวนลิตร", value: `${formatNumber(header.fuel_liter)} ลิตร` },
    { label: "ไมล์เข้า", value: `${numberWithCommas(header.miles_in)} km` },
    { label: "ต้นทุนสายขาย", value: `${formatNumber(header.branch_cost)} บาท` },
    { label: "ระยะทาง", value: `${numberWithCommas(header.distance)} km` },
  ];

  /*
   * จำนวนร้าน is the report's STORED store count — customers that were actually
   * billed. It is not `rows.length`: the grid lists every pathway stop, including
   * stops that produced no bill, so the row count always reads high. That gap is
   * what branches reported.
   *
   * `undefined` and `null` mean different things here and must not be collapsed
   * with a single `??`:
   *   - undefined → the backend predates the field entirely, so the row count is
   *     the only number available. Fall back to it.
   *   - null      → the backend shipped the field and the report genuinely has no
   *     stored count. Falling back would re-introduce the very over-count this
   *     change removes, so show a dash instead.
   */
  const storeCountValue =
    header.customer_count === undefined
      ? numberWithCommas(storeCount)
      : storeCountText(header.customer_count);

  /*
   * ร้านขายสด + ร้านขายเชื่อ can sum ABOVE จำนวนร้าน — a customer billed both ways
   * on the same day is counted in both. Correct by design; not a reconciliation
   * error, and deliberately not "corrected" here.
   *
   * The block stays cols={3} with 5 fields: the store trio fills row 1 exactly, so
   * the split reads as one group, and เป้ายอดขาย / เวลากลับ take row 2 with the
   * GridBlock's existing filler closing the block off square. Widening to cols={5}
   * would fit on one line but would scatter the three related counts across the
   * strip and squeeze every value column on narrow screens.
   */
  const topFields: Field[] = [
    { label: "จำนวนร้าน", value: storeCountValue },
    { label: "ร้านขายสด", value: storeCountText(header.cash_customer_count) },
    {
      label: "ร้านขายเชื่อ",
      value: storeCountText(header.credit_customer_count),
    },
    { label: "เป้ายอดขาย", value: `${formatNumber(header.sales_target)} บาท` },
    { label: "เวลากลับ", value: returnTimeText(header) },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Top strip: colour-coded weekday chip + headline metrics ------------ */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Only the report's OWN weekday is shown (not a 7-day selector rail). */}
        <Typography
          component="span"
          sx={{
            px: 1.5,
            height: 26,
            display: "flex",
            alignItems: "center",
            borderRadius: "6px",
            fontSize: "0.78rem",
            fontWeight: 700,
            color: dayColor.fg,
            bgcolor: dayColor.bg,
            boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
            whiteSpace: "nowrap",
          }}
        >
          {isoDayToThai(header.day_of_week)}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <GridBlock theme={NEUTRAL} cols={3} flex="0 1 auto" fields={topFields} />
      </Box>

      {/* Identity + metric blocks, single dense row that wraps -------------- */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "stretch",
        }}
      >
        <GridBlock
          theme={NEUTRAL}
          cols={2}
          flex="1 1 340px"
          fields={identityFields}
        />

        {/* Quantities + waste + trays + money (amber) */}
        <GridBlock
          theme={AMBER}
          cols={3}
          flex="2 1 460px"
          fields={moneyFields}
        />

        {/* Trip / fuel (blue) */}
        <GridBlock theme={BLUE} cols={2} flex="1 1 280px" fields={tripFields} />
      </Box>
    </Box>
  );
}
